/**
 * Main App Component
 * Manages routing, global state, and application lifecycle
 * 
 * Requirements: 4.2, 4.3, 4.4, 4.5, 15.1
 */

import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthService } from './services/AuthService';
import { SubscriptionService } from './services/SubscriptionService';
import { ThriftLinkService } from './services/ThriftLinkService';
import { PerformanceMonitor } from './utils/PerformanceMonitor';
import { ErrorHandler } from './utils/ErrorHandler';
import ErrorBoundary from './components/ErrorBoundary';
import { TutorialProvider } from './components/TutorialProvider';
import TutorialOverlay from './components/TutorialOverlay';
import Navigation from './components/Navigation';
import Login from './components/Login';
import RegisterPage from './components/RegisterPage';
import OAuthCallback from './components/OAuthCallback';
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import AccountPage from './pages/AccountPage';
import DataDashboard from './pages/DataDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ThriftLinkConfirmPage from './pages/ThriftLinkConfirmPage';
import ToastContainer from './components/ToastContainer';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

// HomeRouteHandler - handles OAuth callback and normal home page access
function HomeRouteHandler({
  isAuthenticated,
  onAuthSuccess,
  creditsRemaining,
  onCreditsUpdate,
  isThriftLinked,
}: {
  isAuthenticated: boolean;
  onAuthSuccess: () => void;
  creditsRemaining: number | null;
  onCreditsUpdate: () => Promise<void>;
  isThriftLinked: boolean;
}) {
  const [searchParams] = useSearchParams();
  const oauthCode = searchParams.get('code');
  const state = searchParams.get('state'); // OAuth state parameter

  console.log('[HomeRouteHandler] Rendering');
  console.log('[HomeRouteHandler] isAuthenticated:', isAuthenticated);
  console.log('[HomeRouteHandler] OAuth code parameter:', oauthCode ? 'present' : 'missing');
  console.log('[HomeRouteHandler] OAuth state parameter:', state ? 'present' : 'missing');
  console.log('[HomeRouteHandler] Full search params:', searchParams.toString());

  // If there's an OAuth code parameter (with state), show the OAuth callback handler
  // OAuth callbacks from Cognito will have both 'code' and 'state' parameters
  if (oauthCode && state) {
    console.log('[HomeRouteHandler] Showing OAuthCallback component');
    return (
      <OAuthCallback
        onSuccess={() => {
          console.log('[HomeRouteHandler] OAuth callback successful, updating auth state');
          onAuthSuccess();
          // OAuthCallback handles navigation (including pending link redirects)
        }}
      />
    );
  }

  // Otherwise, show home page or redirect to login
  if (isAuthenticated) {
    console.log('[HomeRouteHandler] User authenticated, showing HomePage');
    return <HomePage creditsRemaining={creditsRemaining} onCreditsUpdate={onCreditsUpdate} isThriftLinked={isThriftLinked} />;
  }

  console.log('[HomeRouteHandler] Not authenticated, redirecting to login');
  return <Navigate to="/login" replace />;
}

// ThriftLinkRedirect - saves the current URL (with token/callback params) to
// sessionStorage so we can restore it after login, then redirects to /login.
function ThriftLinkRedirect() {
  // Save the full URL including search params so we can restore after login
  sessionStorage.setItem('pendingLinkRedirect', window.location.pathname + window.location.search);
  return <Navigate to="/login" replace />;
}

// Inner component that has access to navigation and tutorial context
function AppContent() {
  // Global state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isThriftLinked, setIsThriftLinked] = useState(false);
  const [subscription, setSubscription] = useState<{ subscription_type: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Services (singleton instances)
  const [authService] = useState(() => AuthService.getInstance());
  const [subscriptionService] = useState(() => new SubscriptionService());
  const [thriftLinkService] = useState(() => new ThriftLinkService());
  const [performanceMonitor] = useState(() => PerformanceMonitor.getInstance());
  const [errorHandler] = useState(() => ErrorHandler.getInstance());

  // Log when credits change
  useEffect(() => {
    console.log('[App] creditsRemaining state changed to:', creditsRemaining);
  }, [creditsRemaining]);

  // Check authentication status on mount
  useEffect(() => {
    // Skip auth check if we're handling an OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthCode = urlParams.has('code') && urlParams.has('state');
    
    if (hasOAuthCode) {
      console.log('[Auth] OAuth callback detected, skipping initial auth check');
      setIsLoading(false);
    } else {
      checkAuthStatus();
    }
    
    // Start performance monitoring - Requirement 16.1, 16.2
    performanceMonitor.startMonitoring(10000); // Check every 10 seconds
    performanceMonitor.onMemoryWarning((metrics) => {
      console.warn('[Performance] High memory usage detected:', metrics);
    });
    
    // Log initial performance metrics
    performanceMonitor.logMetrics();
    
    return () => {
      performanceMonitor.stopMonitoring();
    };
  }, []);

  // Check if user is already authenticated
  const checkAuthStatus = async () => {
    try {
      console.log('[Auth] Checking authentication status...');
      const restored = await authService.restoreSession();
      console.log('[Auth] Session restored:', restored);
      setIsAuthenticated(restored);
      
      // Load credits if authenticated
      if (restored) {
        loadCredits();
      }
    } catch (error) {
      console.log('[Auth] No active session');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Load credits
  const loadCredits = async () => {
    console.log('[App] Loading credits... Current value:', creditsRemaining);
    setIsLoadingCredits(true);
    try {
      const subscriptionData = await subscriptionService.getSubscription();
      console.log('[App] Credits fetched from API:', subscriptionData.credits_remaining);
      console.log('[App] Setting credits state from', creditsRemaining, 'to', subscriptionData.credits_remaining);
      setCreditsRemaining(subscriptionData.credits_remaining);
      setSubscription({ subscription_type: subscriptionData.subscription_type });
      setIsAdmin(subscriptionData.is_admin ?? false);
      console.log('[App] Credits state updated');

      // Load THRIFT link status
      try {
        const linkStatus = await thriftLinkService.getLinkStatus();
        setIsThriftLinked(linkStatus.linked);
      } catch (err) {
        console.warn('[App] Failed to load THRIFT link status:', err);
        setIsThriftLinked(false);
      }
    } catch (err) {
      console.error('[App] Failed to load credits:', err);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  // Handle successful OAuth callback
  const handleAuthSuccess = () => {
    console.log('[Auth] Authentication successful, updating state');
    setIsAuthenticated(true);
    loadCredits();
  };

  // After login, check for a pending THRIFT link redirect saved by ThriftLinkRedirect.
  // This runs after isAuthenticated has been committed to state, so route guards
  // will correctly see the user as authenticated when we navigate.
  // Checks both sessionStorage and localStorage (the latter is a fallback in case
  // a page reload cleared sessionStorage).
  useEffect(() => {
    if (isAuthenticated) {
      const pendingRedirect =
        sessionStorage.getItem('pendingLinkRedirect') ||
        localStorage.getItem('pendingLinkRedirect');
      if (pendingRedirect) {
        console.log('[App] Restoring pending link redirect:', pendingRedirect);
        sessionStorage.removeItem('pendingLinkRedirect');
        localStorage.removeItem('pendingLinkRedirect');
        navigate(pendingRedirect, { replace: true });
      }
    }
  }, [isAuthenticated]);

  // Refresh THRIFT link status when returning from THRIFT redirect
  // (the redirect appends ?thrift_linked=true to /account)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('thrift_linked') === 'true' && isAuthenticated) {
      console.log('[App] THRIFT link redirect detected, refreshing link status');
      loadCredits();
      // Clean up the URL param so it doesn't re-trigger on refresh
      params.delete('thrift_linked');
      const cleanSearch = params.toString();
      navigate(location.pathname + (cleanSearch ? `?${cleanSearch}` : ''), { replace: true });
    }
  }, [location.search, isAuthenticated]);

  // Handle tutorial state across route navigation (Requirement: 5.1)
  useEffect(() => {
    // This effect handles tutorial state when user navigates between pages
    // The tutorial system will automatically handle step filtering based on available UI elements
    console.log('[Tutorial] Route changed to:', location.pathname);
  }, [location.pathname]);

  // Handle logout
  // Requirement 4.5: Preserve auth state across navigation
  const handleLogout = async () => {
    try {
      // Logout from auth service
      await authService.logout();

      // Update state
      setIsAuthenticated(false);

      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      errorHandler.handleError(error as Error, { component: 'logout' });
    }
  };

  // Show loading screen while checking auth
  if (isLoading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" message="Loading application..." />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <TutorialProvider>
        <div className="App">
          {/* Toast notifications */}
          <ToastContainer position="top-right" />
          
          {/* Navigation - shown only when authenticated */}
          {/* Requirement 4.1: Display navigation menu */}
          {isAuthenticated && (
              <Navigation
                onLogout={handleLogout}
                creditsRemaining={creditsRemaining}
                isLoadingCredits={isLoadingCredits}
                subscription={subscription}
                isAdmin={isAdmin}
              />
            )}
          
          <Routes>
            {/* Login route */}
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Login />
                )
              }
            />

            {/* Register route */}
            <Route
              path="/register"
              element={
                isAuthenticated ? (
                  <Navigate to="/home" replace />
                ) : (
                  <RegisterPage />
                )
              }
            />

            {/* Home route (protected) - also handles OAuth callback */}
            {/* Requirement 4.2: Home page with camera and results */}
            <Route
              path="/home"
              element={
                <HomeRouteHandler
                  isAuthenticated={isAuthenticated}
                  onAuthSuccess={handleAuthSuccess}
                  creditsRemaining={creditsRemaining}
                  onCreditsUpdate={loadCredits}
                  isThriftLinked={isThriftLinked}
                />
              }
            />

            {/* Root redirect to home */}
            <Route
              path="/"
              element={<Navigate to="/home" replace />}
            />

            {/* Categories route (protected) */}
            {/* Requirement 4.3: Categories page for editing criteria */}
            <Route
              path="/categories"
              element={
                isAuthenticated ? (
                  <CategoriesPage />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Data Dashboard route (protected - Business+ only) */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  <DataDashboard />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Account route (protected) */}
            {/* Requirement 4.4: Account page for subscription management */}
            <Route
              path="/account"
              element={
                isAuthenticated ? (
                  <AccountPage />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* THRIFT-initiated account linking (protected) */}
            <Route
              path="/link-thrift"
              element={
                isAuthenticated ? (
                  <ThriftLinkConfirmPage />
                ) : (
                  <ThriftLinkRedirect />
                )
              }
            />

            {/* Admin dashboard (protected, admin-only) */}
            <Route
              path="/admin"
              element={
                isAuthenticated && isAdmin ? (
                  <AdminDashboard />
                ) : isAuthenticated ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
          
          {/* Tutorial Overlay - shown when tutorial is active */}
          <TutorialOverlay />
        </div>
      </TutorialProvider>
    </ErrorBoundary>
  );
}

// Main App component with Router wrapper
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
